import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_SECTION_MARGINS, SectionMargins, ImageContentSectionContent } from "api/shop";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";
import {
  HeadingTagSelect,
  PaziaFormButton,
  PaziaInput,
  PaziaSelect,
  PaziaFileDropzone,
  PaziaTextarea,
} from "components/atoms";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MdAdd, MdDelete } from "react-icons/md";
import * as yup from "yup";
import { ANIMATION_OPTIONS } from "./animationOptions";
import { ButtonVariantSelect } from "./ButtonVariantSelect";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";
import { useShopImageUpload } from "./useShopImageUpload";

interface ImageContentFormProps {
  onSubmit: (data: ImageContentFormValues) => void;
  onCancel: () => void;
  defaultValues?: ImageContentSectionContent;
  isLoading?: boolean;
}

export const ImageContentForm = ({ onSubmit, onCancel, defaultValues, isLoading }: ImageContentFormProps) => {
  const { uploadImage } = useShopImageUpload();
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.ANIMATION]: defaultValues?.animation ?? "fade-bottom",
      [FORM_NAMES.IMAGE_SRC]: defaultValues?.image?.src ?? "",
      [FORM_NAMES.IMAGE_ALT]: defaultValues?.image?.alt ?? "",
      [FORM_NAMES.IMAGE_WIDTH]: defaultValues?.image?.width ?? "",
      [FORM_NAMES.IMAGE_HEIGHT]: defaultValues?.image?.height ?? "",
      [FORM_NAMES.IMAGE_ROUNDING]: defaultValues?.image?.rounding ?? "none",
      [FORM_NAMES.IMAGE_POSITION]: defaultValues?.image?.objectPosition ?? "center",
      [FORM_NAMES.HEADER_TEXT]: defaultValues?.header?.text ?? "",
      [FORM_NAMES.HEADER_COLOR]: defaultValues?.header?.color ?? "",
      [FORM_NAMES.HEADER_TAG]: defaultValues?.header?.tag ?? "h2",
      [FORM_NAMES.CONTENT_DATA]: defaultValues?.content?.data ?? "",
      [FORM_NAMES.CONTENT_PADDING]: defaultValues?.contentPadding ?? "md",
      [FORM_NAMES.BG_STYLE]: defaultValues?.bgStyle ?? "",
      [FORM_NAMES.BUTTONS]: defaultValues?.buttons ?? [],
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control } = methods;

  const {
    fields: buttonFields,
    append: appendButton,
    remove: removeButton,
  } = useFieldArray({
    control,
    name: FORM_NAMES.BUTTONS,
  });

  const handleFormSubmit = async (data: FormValues) => {
    const uploadedSrc = await uploadImage(data.imageSrc);
    const formattedData: ImageContentFormValues = {
      animation: data.animation,
      image: {
        src: uploadedSrc,
        alt: data.imageAlt,
        width: data.imageWidth || undefined,
        height: data.imageHeight || undefined,
        rounding: data.imageRounding || undefined,
        objectPosition: data.imageObjectPosition || undefined,
      },
      header: { text: data.headerText, color: data.headerColor || undefined, tag: data.headerTag as any },
      content: { data: data.contentData },
      contentPadding: data.contentPadding || undefined,
      bgStyle: data.bgStyle || "",
      buttons: data.buttons.map((btn) => ({
        label: btn.label,
        variant: btn.variant,
        href: btn.href,
      })),
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  const addButton = () => {
    appendButton({ label: "", variant: "primary", href: "" });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <PaziaSelect
          name={FORM_NAMES.ANIMATION}
          label="Animacja"
          required
          options={ANIMATION_OPTIONS}
        />

        {/* Image */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Obraz (lewa strona)</h4>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="[&_img]:max-h-48">
              <PaziaFileDropzone
                name={FORM_NAMES.IMAGE_SRC}
                label="Obraz"
                type="image"
                single
                preview="image"
              />
            </div>
            <div className="grid gap-3">
              <PaziaInput
                name={FORM_NAMES.IMAGE_ALT}
                label="Tekst alternatywny (alt)"
              />
              <div className="grid grid-cols-2 gap-3">
                <PaziaInput
                  name={FORM_NAMES.IMAGE_WIDTH}
                  label="Szerokość (CSS)"
                  placeholder="np. 50%, 400px"
                />
                <PaziaInput
                  name={FORM_NAMES.IMAGE_HEIGHT}
                  label="Wysokość (CSS)"
                  placeholder="np. 100%, 500px"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <PaziaSelect name={FORM_NAMES.IMAGE_ROUNDING} label="Zaokrąglenie" options={IMAGE_ROUNDING_OPTIONS} />
                <PaziaSelect name={FORM_NAMES.IMAGE_POSITION} label="Pozycja obrazu" options={IMAGE_POSITION_OPTIONS} />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Treść (prawa strona)</h4>
          <div className="grid gap-3">
            <div className="grid gap-3 md:grid-cols-2">
              <PaziaInput name={FORM_NAMES.HEADER_TEXT} label="Nagłówek" />
              <PaziaInput
                name={FORM_NAMES.HEADER_COLOR}
                label="Kolor nagłówka (CSS)"
                placeholder="np. #000000, rgb(0,0,0)"
              />
            </div>
            <HeadingTagSelect name={FORM_NAMES.HEADER_TAG} />
            <PaziaSelect name={FORM_NAMES.CONTENT_PADDING} label="Wypełnienie treści" options={CONTENT_PADDING_OPTIONS} />
            <PaziaFromQuill name={FORM_NAMES.CONTENT_DATA} label="Treść" required />
          </div>
        </div>

        {/* Background Style */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Tło sekcji</h4>
          <PaziaTextarea
            name={FORM_NAMES.BG_STYLE}
            label="Styl tła (CSS)"
            rows={2}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Przykłady: <code>#ffffff</code>, <code>transparent</code>, <code>linear-gradient(150deg, #F3F5F7 0%, #00ACBF 100%)</code>
          </p>
        </div>

        {/* Buttons Section */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Przyciski</h4>
            <button
              type="button"
              onClick={addButton}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj przycisk
            </button>
          </div>

          {buttonFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak przycisków. Kliknij "Dodaj przycisk", aby dodać.
            </p>
          ) : (
            <div className="space-y-3">
              {buttonFields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-800"
                >
                  <div className="flex-1">
                    <PaziaInput
                      name={`${FORM_NAMES.BUTTONS}.${index}.label`}
                      label="Etykieta"
                      required
                    />
                  </div>
                  <div className="w-40">
                    <ButtonVariantSelect
                      name={`${FORM_NAMES.BUTTONS}.${index}.variant`}
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <PaziaInput
                      name={`${FORM_NAMES.BUTTONS}.${index}.href`}
                      label="Link (href)"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeButton(index)}
                    className="mb-1 rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                    title="Usuń przycisk"
                  >
                    <MdDelete size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

const FORM_NAMES = {
  ANIMATION: "animation",
  IMAGE_SRC: "imageSrc",
  IMAGE_ALT: "imageAlt",
  IMAGE_WIDTH: "imageWidth",
  IMAGE_HEIGHT: "imageHeight",
  IMAGE_ROUNDING: "imageRounding",
  IMAGE_POSITION: "imageObjectPosition",
  HEADER_TEXT: "headerText",
  HEADER_COLOR: "headerColor",
  HEADER_TAG: "headerTag",
  CONTENT_DATA: "contentData",
  CONTENT_PADDING: "contentPadding",
  BG_STYLE: "bgStyle",
  BUTTONS: "buttons",
} as const;

const CONTENT_PADDING_OPTIONS = [
  { value: "none", label: "Brak" },
  { value: "sm", label: "Mały" },
  { value: "md", label: "Średni (domyślny)" },
  { value: "lg", label: "Duży" },
];

const IMAGE_ROUNDING_OPTIONS = [
  { value: "none", label: "Brak" },
  { value: "sm", label: "Mały" },
  { value: "md", label: "Średni" },
  { value: "lg", label: "Duży" },
  { value: "full", label: "Pełny (koło)" },
];

const IMAGE_POSITION_OPTIONS = [
  { value: "center", label: "Środek" },
  { value: "top", label: "Góra" },
  { value: "bottom", label: "Dół" },
  { value: "left", label: "Lewo" },
  { value: "right", label: "Prawo" },
];

const buttonSchema = yup.object({
  label: yup.string().required("Etykieta jest wymagana"),
  variant: yup.string().required("Wariant jest wymagany"),
  href: yup.string().required("Link jest wymagany"),
});

const schema = yup
  .object({
    [FORM_NAMES.ANIMATION]: yup.string().required("Animacja jest wymagana"),
    [FORM_NAMES.IMAGE_SRC]: yup.mixed().required("Obraz jest wymagany"),
    [FORM_NAMES.IMAGE_ALT]: yup.string(),
    [FORM_NAMES.IMAGE_WIDTH]: yup.string(),
    [FORM_NAMES.IMAGE_HEIGHT]: yup.string(),
    [FORM_NAMES.IMAGE_ROUNDING]: yup.string(),
    [FORM_NAMES.IMAGE_POSITION]: yup.string(),
    [FORM_NAMES.HEADER_TEXT]: yup.string(),
    [FORM_NAMES.HEADER_COLOR]: yup.string(),
    [FORM_NAMES.HEADER_TAG]: yup.string(),
    [FORM_NAMES.CONTENT_DATA]: yup.string().required("Treść jest wymagana"),
    [FORM_NAMES.CONTENT_PADDING]: yup.string(),
    [FORM_NAMES.BG_STYLE]: yup.string(),
    [FORM_NAMES.BUTTONS]: yup.array().of(buttonSchema),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface FormValues {
  animation: string;
  imageSrc: string | File;
  imageAlt: string;
  imageWidth: string;
  imageHeight: string;
  imageRounding: string;
  imageObjectPosition: string;
  headerText: string;
  headerColor: string;
  headerTag: string;
  contentData: string;
  contentPadding: string;
  bgStyle: string;
  buttons: Array<{ label: string; variant: "primary" | "secondary" | "outline"; href: string }>;
  margins: SectionMargins;
}

export interface ImageContentFormValues {
  animation: string;
  image: { src: string; alt: string; width?: string; height?: string; rounding?: string; objectPosition?: string };
  header: { text: string; color?: string; tag?: string };
  content: { data: string };
  contentPadding?: string;
  bgStyle: string;
  buttons: Array<{
    label: string;
    variant: "primary" | "secondary" | "outline";
    href: string;
  }>;
  margins?: SectionMargins;
}
